import { useQuery, type QueryClient } from '@tanstack/react-query'
import { type UserMe } from 'shared/types/user'
import apiClient from './axiosBase'

/** ログイン中ユーザーの query key。認証フローとアプリ全体で共有する。 */
export const userMeQueryKey = ['userMe'] as const

/**
 * ログイン中ユーザーを取得する。
 *
 * status が INVITED のユーザーは、招待直後で本人情報が未登録のため
 * activate を一度通してからでないと以降の API が使えない。activate は更新系だが、
 * CSRF トークンの取得は apiClient の request interceptor が行うためここでは扱わない。
 * activate のレスポンスは使わず、最初に取得した me をそのまま返す。
 *
 * ここで欲しいのは status の遷移だけなので氏名は送らない。空文字を送ると登録済みの
 * 氏名を意図せず上書きしてしまう。氏名は登録フォーム
 * （NewPasswordRegistrationForm、4 項目とも必須）とアカウント設定が書く。
 */
export async function fetchUserMe (): Promise<UserMe> {
  const res = await apiClient.get<UserMe>('/v1/users/me')
  const me = res.data

  if (me.status === 'INVITED') {
    await apiClient.put(`/v1/tenants/${me.tenants[0].id}/users/${me.id}/activate`, {})
  }

  return me
}

/**
 * ログイン中ユーザーの query。
 *
 * server state だが取得タイミングは認証フロー(loggedIn / ログイン成功時)が制御するため、
 * `enabled: false` で自動 fetch を止め、認証側が `queryClient.fetchQuery` /
 * `setQueryData` でキャッシュへ投入する。各画面はこのキャッシュを購読するだけ。
 * ログイン中は不変とみなし stale/gc しない。
 */
export const useUserMeQuery = () =>
  useQuery({
    queryKey: userMeQueryKey,
    queryFn: fetchUserMe,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity
  })

/**
 * ログイン中ユーザーを読む薄いフック。
 * 旧 `useAtomValue(userMeState)` と同義に `UserMe | null` を返す。
 */
export const useUserMe = (): UserMe | null => useUserMeQuery().data ?? null

/** 認証フローからキャッシュへ userMe を反映する。 */
export const setUserMeCache = (queryClient: QueryClient, userMe: UserMe | null): void => {
  queryClient.setQueryData(userMeQueryKey, userMe)
}

/** 認証フローから userMe を取得してキャッシュへ入れる(取得結果も返す)。 */
export const loadUserMe = async (queryClient: QueryClient): Promise<UserMe> =>
  await queryClient.fetchQuery({ queryKey: userMeQueryKey, queryFn: fetchUserMe })

/** ログイン中ユーザーが社内アカウント(jizaie.co.jp)かどうか。 */
export const isJizaieEmployee = (userMe: UserMe | null): boolean => {
  const email = userMe?.email
  if (email == null || email === '') return false
  const parts = email.split('@')
  return parts.length === 2 && parts[1] === 'jizaie.co.jp'
}
